import { CsvColumn } from './csv';

// download rows as a simple table PDF. jspdf loads on demand, so the main
// bundle stays small. reuses the CSV column definitions (key + label).
export async function downloadPdf(
  fileName: string,
  title: string,
  columns: CsvColumn[],
  rows: any[],
  subtitle?: string
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(14);
  doc.text(title, 14, 16);
  doc.setFontSize(10);
  const stamp = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  doc.text(subtitle ? `${subtitle} — generated ${stamp}` : `Generated ${stamp}`, 14, 23);

  const cell = (row: any, key: string): string => {
    const value = key.split('.').reduce((acc: any, part) => (acc == null ? acc : acc[part]), row);
    if (value === null || value === undefined) return '';
    return Array.isArray(value) ? value.join('; ') : String(value);
  };

  autoTable(doc, {
    startY: 28,
    head: [columns.map((c) => c.label)],
    body: rows.map((r) => columns.map((c) => cell(r, c.key))),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [70, 60, 50] },
  });

  doc.save(fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
}

// the booking slip: a small document with the booking facts, the guests, and
// the allocated rooms. `booking` is the by-id read (requisition + guests +
// provisionDetails).
export async function downloadBookingSlip(booking: any): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF();
  doc.setFontSize(15);
  doc.text(`Booking slip — #${booking.requisitionId}`, 14, 16);
  doc.setFontSize(10);
  const stamp = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  doc.text(`Generated ${stamp}`, 14, 23);

  autoTable(doc, {
    startY: 28,
    theme: 'plain',
    styles: { fontSize: 10 },
    body: [
      ['Family code', booking.familyCode || '-'],
      ['Booking', `${booking.requisitionCategory || ''} · ${booking.requisitionType || ''} · ${booking.requisitionMode || ''}`],
      ['Stay', `${booking.requisitionFromDate || ''} to ${booking.requisitionToDate || ''}`],
      ['Guests', String(booking.requisitionGuestCount ?? '')],
      ['Status', `${booking.requisitionStatus || ''} / ${booking.requisitionLifecycleStatus || ''}`],
    ],
  });

  const afterFacts = (doc as any).lastAutoTable?.finalY ?? 60;
  const guests = booking.guests || [];
  if (guests.length) {
    autoTable(doc, {
      startY: afterFacts + 6,
      head: [['Guest', 'Type', 'Gender']],
      body: guests.map((g: any) => [g.name || '-', g.guestType || '-', g.gender || '-']),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [70, 60, 50] },
    });
  }

  const afterGuests = (doc as any).lastAutoTable?.finalY ?? afterFacts;
  const rooms = booking.provisionDetails || [];
  if (rooms.length) {
    autoTable(doc, {
      startY: afterGuests + 6,
      head: [['Guest house', 'Room', 'Type', 'Floor', 'Capacity']],
      body: rooms.map((r: any) => [
        r.guestHouseName || '-',
        r.roomCode || '-',
        r.accommoType || '-',
        String(r.floorNumber ?? ''),
        String(r.totalCapacity ?? ''),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [70, 60, 50] },
    });
  }

  doc.save(`booking-${booking.requisitionId}.pdf`);
}
